/**
 * Supplies the model keys the transpiler's binding overlay injects into the reference composer (`ViewModelExtras`):
 * the input's value/handlers and the suggestion chips. Text goes through the same orchestrator call as voice (R-02).
 */
import { useCallback, useMemo, useState, type SyntheticEvent } from 'react';
import type { ViewModel } from '@/screens/generated/view-model';
import type { VoiceApi } from './useVoice';

export function useComposerBindings(m: ViewModel, voice: VoiceApi): ViewModel {
  const [query, setQuery] = useState('');
  const composerSet = useCallback((e?: SyntheticEvent) => {
    const t = e?.target as HTMLInputElement | undefined;
    setQuery(t?.value ?? '');
  }, []);
  const composerAsk = useCallback(
    (text: string) => {
      const t = text.trim();
      if (!t) return;
      setQuery('');
      void voice.ask(t);
    },
    [voice],
  );
  const composerKey = useCallback(
    (e?: SyntheticEvent) => {
      const ev = e as unknown as { key?: string; currentTarget?: HTMLInputElement } | undefined;
      if (ev?.key !== 'Enter') return;
      composerAsk(ev.currentTarget?.value ?? query);
    },
    [composerAsk, query],
  );
  return useMemo(
    () => ({ ...m, composerQuery: query, composerSet, composerKey, composerAsk }),
    [m, query, composerSet, composerKey, composerAsk],
  );
}
